import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDeleteProjectMutation } from "features/Projects/api/useProjectsMutations"
import { useKindeAuth } from "@kinde-oss/kinde-auth-react"

import { useProjectStore, Project } from "stores/projectStore"
import { useUserStore, User } from "stores/userStore"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/free-solid-svg-icons"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import CopyProjectModal from "./CopyProjectModal.component"
import ProjectMembersModal from "./ProjectMembers/ProjectMembersModal.component"

import MoveProjectModal from "../MoveProjectModal.component"
import {
  hasAdminPrivileges,
  hasOwnershipPrivileges,
} from "types/userRole.types"
import { KindeRole, KindeRolesClaim } from "types/kinde.types"

/**
 * Helper function to check if a user can perform admin actions on a project
 * @param project - The project to check
 * @param user - The user to check against
 * @param userRoles - The user's roles from Kinde auth
 * @returns boolean indicating if the user can perform admin actions
 */
export const canPerformProjectAdminActions = (
  project: Project,
  user: User,
  userRoles: KindeRole[]
): boolean => {
  const hasAdminRole =
    userRoles?.some((role: KindeRole) => hasAdminPrivileges(role.key)) || false

  return hasAdminRole || hasOwnershipPrivileges(project, user)
}

interface ProjectActionsProps {
  userData: string[]
}

/**
 * Renders the actions available for a project.
 *
 * This component renders buttons for actions such as deleting the project,
 * archiving it, and managing members.
 *
 * @component
 * @param props - The component props
 * @param props.userData - Array of available users in the unit
 * @returns The rendered ProjectActions component.
 */
const ProjectActions: React.FC<ProjectActionsProps> = ({ userData }) => {
  const deleteProjectMutation = useDeleteProjectMutation()
  const navigate = useNavigate()
  const project = useProjectStore((state) => state.project)
  const user = useUserStore((state) => state.user)

  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false)
  const { getClaim } = useKindeAuth()

  // Get and type the user roles from Kinde
  const [userRoles, setUserRoles] = useState<KindeRole[]>([])

  useEffect(() => {
    const loadUserRoles = async () => {
      try {
        const userRolesClaim = await getClaim("roles") as KindeRolesClaim | null
        setUserRoles(userRolesClaim?.value || [])
      } catch (error) {
        console.error("Failed to load user roles:", error)
        setUserRoles([])
      }
    }
    loadUserRoles()
  }, [getClaim])

  // Check user permissions using helper functions
  const canPerformAdminActions = canPerformProjectAdminActions(
    project,
    user,
    userRoles
  )

  /**
   * Handles the project deletion process. Notifications (success, business
   * error, transport error) are handled by the delete mutation.
   *
   * @async
   * @function deleteProject
   * @param {React.MouseEvent<HTMLButtonElement>} e - The event object.
   * @returns {Promise<void>} A promise that resolves when the project is successfully deleted or rejects if an error occurs.
   */
  const deleteProject = async (
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.preventDefault()
    try {
      await deleteProjectMutation.mutateAsync(project.id)
      navigate("/home")
    } catch (error) {
      console.error("Error deleting project:", error)
    }
  }

  /**
   * Handles showing the delete confirmation modal
   */
  const handleShowDeleteModal = (): void => {
    setIsDeleteModalVisible(true)
  }

  /**
   * Handles hiding the delete confirmation modal
   */
  const handleHideDeleteModal = (): void => {
    setIsDeleteModalVisible(false)
  }

  return (
    <>
      <ConfirmationModal
        title="Delete Project"
        isVisible={isDeleteModalVisible}
        cancel={handleHideDeleteModal}
        confirm={deleteProject}
        confirmButtonText="Delete"
        confirmButtonType="danger"
        message="Are you sure you want to delete this project? This action cannot be undone."
      />

      <div className="flex flex-wrap gap-1 justify-start items-center">
        {/* Copy and Move actions - only for admins or owners */}
        {canPerformAdminActions && <CopyProjectModal userData={userData} />}
        {canPerformAdminActions && <MoveProjectModal userData={userData} />}

        {/* Delete action - only for admins or owners */}
        {canPerformAdminActions && (
          <DataQButton
            onClick={handleShowDeleteModal}
            type="primary"
            isLoading={deleteProjectMutation.isPending}
            isDisabled={deleteProjectMutation.isPending}
          >
            Delete Project
            <FontAwesomeIcon className="ml-2" icon={faTrash} />
          </DataQButton>
        )}

        {/* Project members management - always visible for project members */}
        <ProjectMembersModal userData={userData} />
      </div>
    </>
  )
}

export default ProjectActions
